import { useMemo, type ReactNode, useState, useEffect, useRef, useCallback } from "react";

import MenuOverlay from "./MenuOverlay";
import { MaterialNames } from "../material_symbol_names";
import type { MdMenu } from "./LitMenu";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import useMenuChordKeys from "@/hooks/useMenuChordKeys";

/** Non-interactive section label row (e.g. “Manage”, “Actions”). */
export type MenuHeadline = {
  type: "headline";
  label: string;
  useDivider?: boolean;
  dataTestId?: string;
};

export type MenuItem = {
  type?: "item";
  label: ReactNode;
  supportingText?: string;
  onClick?(): void;
  disabled?: boolean;
  /**
   * Render the row as display-only (no hover, no click, default cursor) without dimming.
   * Use for identity rows or read-only metadata. Prefer this over `disabled` when the row
   * is informational rather than a permission-blocked action.
   */
  nonInteractive?: boolean;
  /** Render the row in the error color to signal a destructive action (Delete, Remove, etc.). */
  destructive?: boolean;
  iconName?: MaterialNames;
  itemClassName?: string;
  useDivider?: boolean;
  dataTestId?: string;
  /** Explicit accessible name for analytics and screen readers when label text alone is insufficient. */
  ariaLabel?: string;
  showBadge?: boolean;
  copyable?: boolean;
  copyText?: string;
  /** Analytics-safe field name for the trailing copy control. */
  copyFieldLabel?: string;
  /** Internal system identifier for Amplitude debug attributes on allowlisted ID fields. */
  copyFieldId?: string;
  /** Menu only. Snackbar text for the trailing copy control when feedback is snackbar. */
  copySuccessMessage?: string;
  supportingTextWrap?: boolean;
  /** Keyboard shortcut hint for the menu row (e.g. `/`). */
  shortcutKey?: string;
  /** When false, show only the key label (single-key shortcuts such as `/`). Defaults to true. */
  shortcutShowModifier?: boolean;
  /** When true, non-Mac hints include Shift (e.g. Ctrl+Shift+/ for ⌘?). */
  shortcutImplicitShift?: boolean;
  /** Single-key chord while the menu is open; shown as a key-cap hint beside the label (e.g. `i`, `n`, `t`). */
  chordKey?: string;
  /** Keep the leading icon column width when `iconName` is omitted (e.g. backend source radio rows). */
  reserveLeadingIconSpace?: boolean;
};

/** One row in {@link MenuProps.menuItems}: an action item or a non-interactive section headline. */
export type MenuListEntry = MenuHeadline | MenuItem;

type MenuProps = {
  menuItems: MenuListEntry[];
  yOffset?: number;
  /** Title shown at the top of the mobile side sheet; ignored on desktop. */
  title?: string;
  /** Optional class override for the desktop menu panel. Detail rows widen automatically when omitted. */
  menuClassName?: string;
  AnchorComponent: ReactNode;
  /** Controlled open state for keyboard shortcuts that open the menu imperatively. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** When false, chord key caps are not shown beside item labels (e.g. click-opened menus). */
  showChordKeyCaps?: boolean;
  /** Desktop arrow-key axis for roving focus (navbar menus use horizontal). */
  arrowNavigationAxis?: "vertical" | "horizontal";
};

const randomNum = () => (Math.random() * 10000000).toFixed(0);

export default function Menu({
  menuItems,
  yOffset = 2,
  title,
  menuClassName,
  AnchorComponent,
  open: openProp,
  onOpenChange,
  showChordKeyCaps = true,
  arrowNavigationAxis = "vertical"
}: MenuProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : uncontrolledOpen;
  const menuRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const mdMenuRef = useRef<MdMenu>(null);
  const { isCompactLayout } = useIsCompactLayout();

  const setOpen = useCallback(
    (next: boolean | ((prev: boolean) => boolean)) => {
      if (!isControlled) {
        setUncontrolledOpen((prev) => {
          const resolved = typeof next === "function" ? next(prev) : next;
          onOpenChange?.(resolved);
          return resolved;
        });
        return;
      }

      const resolved = typeof next === "function" ? next(open) : next;
      onOpenChange?.(resolved);
    },
    [isControlled, onOpenChange, open]
  );

  const closeMenu = useCallback(() => setOpen(false), [setOpen]);

  useMenuChordKeys({
    open,
    menuItems,
    onClose: closeMenu,
    menuRef: mdMenuRef,
    enableArrowNavigation: !isCompactLayout,
    arrowNavigationAxis
  });

  function setVisibility(e?: React.MouseEvent) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setOpen((prev) => !prev);
  }

  // Click-outside handling lives here (not in MenuOverlay) because it dismisses
  // the desktop popover; on mobile the SideSheetModal owns its own dismissal.
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    if (open && !isCompactLayout) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, isCompactLayout, setOpen]);

  const anchorId = useMemo(() => `menu-anchor-${randomNum()}`, []);

  return (
    <div
      ref={menuRef}
      className="relative cursor-pointer"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <div ref={anchorRef} id={anchorId} onClick={setVisibility}>
        {!!AnchorComponent && AnchorComponent}
      </div>

      <MenuOverlay
        ref={mdMenuRef}
        menuItems={menuItems}
        open={open}
        setOpen={setOpen}
        onClosed={closeMenu}
        anchorId={anchorId}
        yOffset={yOffset}
        mobileTitle={title}
        menuClassName={menuClassName}
        showChordKeyCaps={showChordKeyCaps}
      />
    </div>
  );
}
